// ============================================================================
// 📦 VERSION: 2025.12.27.0200
// ============================================================================
// TRAK Quiz Game - Auth Component (Login/Register)
// ============================================================================
// CHANGELOG:
// 2025.12.19.1630 - Added "Return to Game" link on logged-in profile view
// 2025.12.19.1545 - Added Forgot Password link and form on login screen
// 2025.12.12.1000 - Initial version with phone input
// ============================================================================
import React, { useState } from 'react';
import { login, register, isLoggedIn, logout } from '../api';
import PhoneInput from './PhoneInput';

const API_URL = 'https://api.traktag.com/api';

const AuthScreen = ({ onAuthSuccess, playerData, initialMode = 'login' }) => {
  const [mode, setMode] = useState(initialMode); // 'login' or 'register'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [phoneData, setPhoneData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  // 🆕 2025.12.19: Forgot Password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSuccess, setForgotSuccess] = useState('');
  const [forgotError, setForgotError] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);

  // Update mode when initialMode prop changes
  React.useEffect(() => {
    setMode(initialMode);
    setShowForgotPassword(false); // Reset forgot password when mode changes
  }, [initialMode]);
  
  // 🆕 2025.12.19: Handle Forgot Password
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotError('');
    setForgotSuccess('');
    
    if (!forgotEmail) {
      setForgotError('Please enter your email address');
      return;
    }
    
    setForgotLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setForgotSuccess('If an account exists with this email, a reset link has been sent.');
        setForgotEmail('');
      } else {
        setForgotError(data.error || 'Failed to send reset email');
      }
    } catch (err) {
      console.error('Forgot password error:', err);
      setForgotError('Connection failed. Please try again.');
    }
    
    setForgotLoading(false);
  };

  // If already logged in, show profile
  if (isLoggedIn() && playerData) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <div className="bg-gray-800 rounded-2xl p-6 w-full max-w-sm">
          <div className="text-center mb-6">
            <div className="w-20 h-20 bg-gradient-to-r from-blue-500 to-teal-500 rounded-full mx-auto mb-4 flex items-center justify-center">
              <span className="text-3xl">👤</span>
            </div>
            <h2 className="text-white text-xl font-bold">{playerData.name}</h2>
            <p className="text-gray-400 text-sm">{playerData.email}</p>
          </div>
          
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-gray-700 rounded-lg p-3 text-center">
              <p className="text-yellow-400 text-2xl font-bold">{(playerData.stats?.totalTraks || playerData.total_traks || 0).toLocaleString()}</p>
              <p className="text-gray-400 text-xs">TRAKs</p>
            </div>
            <div className="bg-gray-700 rounded-lg p-3 text-center">
              <p className="text-orange-400 text-2xl font-bold">{playerData.stats?.currentStreak || playerData.current_streak || 0}</p>
              <p className="text-gray-400 text-xs">Day Streak</p>
            </div>
          </div>
          
          <button
            onClick={() => {
              logout();
              onAuthSuccess(null);
            }}
            className="w-full bg-red-600 hover:bg-red-500 text-white font-bold py-3 rounded-lg"
          >
            Logout
          </button>
          
          {/* Return to Game link */}
          <button
            onClick={() => onAuthSuccess(playerData)}
            className="w-full mt-3 text-yellow-400 hover:text-yellow-300 text-sm font-medium"
          >
            Return to Game
          </button>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      let result;
      if (mode === 'login') {
        result = await login(email, password);
      } else {
        if (!name.trim()) {
          setError('Please enter your name');
          setLoading(false);
          return;
        }
        result = await register(email, password, name, phoneData);
      }

      if (result.error) {
        setError(result.error);
      } else if (result.player) {
        // 🆕 Check for pal referral bonus after successful registration
        if (mode === 'register') {
          const palCode = localStorage.getItem('palReferralCode');
          const palBonus = localStorage.getItem('palBonusAmount');
          
          if (palCode) {
            try {
              const token = localStorage.getItem('trak_token');
              const response = await fetch(`${API_URL}/pal/claim-referral`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                  code: palCode,
                  bonus_amount: parseInt(palBonus) || 50
                })
              });
              
              const data = await response.json();
              if (data.success) {
                console.log('✅ Referral bonus claimed:', data.bonus_traks);
                // Update player's TRAKs in result
                result.player.total_traks = (result.player.total_traks || 0) + data.bonus_traks;
              }
            } catch (err) {
              console.error('Failed to claim referral bonus:', err);
            }
            
            // Clear referral data
            localStorage.removeItem('palReferralCode');
            localStorage.removeItem('palBonusAmount');
            localStorage.removeItem('palHelperName');
          }
        }
        
        onAuthSuccess(result.player);
      }
    } catch (err) {
      setError('Connection failed. Please try again.');
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
      <div className="bg-gray-800 rounded-2xl p-6 w-full max-w-sm">
        {/* Logo/Header */}
        <div className="text-center mb-6">
          <h1 className="text-2xl font-black">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-orange-500 to-blue-500">TRAK</span>
            <span className="text-white">tag</span>
          </h1>
          <p className="text-gray-400 text-sm mt-1">Quiz Game</p>
        </div>

        {/* 🆕 2025.12.19: Forgot Password Form */}
        {showForgotPassword ? (
          <div>
            <h2 className="text-white text-lg font-bold mb-2">Reset Password</h2>
            <p className="text-gray-400 text-sm mb-4">
              Enter your email address and we will send you a link to reset your password.
            </p>
            
            {forgotError && (
              <div className="bg-red-600/20 border border-red-500 text-red-400 px-4 py-2 rounded-lg mb-4 text-sm">
                {forgotError}
              </div>
            )}
            
            {forgotSuccess && (
              <div className="bg-green-600/20 border border-green-500 text-green-400 px-4 py-2 rounded-lg mb-4 text-sm">
                {forgotSuccess}
              </div>
            )}
            
            <form onSubmit={handleForgotPassword}>
              <div className="mb-4">
                <label className="text-gray-400 text-sm block mb-1">Email</label>
                <input
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  className="w-full bg-gray-700 text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <button
                type="submit"
                disabled={forgotLoading}
                className={`w-full font-bold py-3 rounded-lg transition mb-3 ${
                  forgotLoading
                    ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-yellow-600 to-orange-600 hover:from-yellow-500 hover:to-orange-500 text-white'
                }`}
              >
                {forgotLoading ? 'Sending...' : 'Send Reset Link'}
              </button>
            </form>
            
            <button
              onClick={() => {
                setShowForgotPassword(false);
                setForgotError('');
                setForgotSuccess('');
              }}
              className="w-full text-blue-400 hover:text-blue-300 text-sm"
            >
              Back to Login
            </button>
          </div>
        ) : (
          <>
            {/* Toggle Login/Register */}
            <div className="flex bg-gray-700 rounded-lg p-1 mb-6">
              <button
                onClick={() => setMode('login')}
                className={`flex-1 py-2 rounded-md text-sm font-bold transition ${
                  mode === 'login' 
                    ? 'bg-blue-600 text-white' 
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                Login
              </button>
              <button
                onClick={() => setMode('register')}
                className={`flex-1 py-2 rounded-md text-sm font-bold transition ${
                  mode === 'register' 
                    ? 'bg-blue-600 text-white' 
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                Register
              </button>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-600/20 border border-red-500 text-red-400 px-4 py-2 rounded-lg mb-4 text-sm">
                {error}
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit}>
              {mode === 'register' && (
                <>
                  {/* Name Field */}
                  <div className="mb-4">
                    <label className="text-gray-400 text-sm block mb-1">Name</label>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Your display name"
                      className="w-full bg-gray-700 text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  {/* Phone Input */}
                  <div className="mb-4">
                    <PhoneInput
                      label="Phone Number (Optional)"
                      onChange={(data) => setPhoneData(data)}
                      defaultCountry="GB"
                      placeholder="Phone number"
                    />
                  </div>
                </>
              )}
              
              <div className="mb-4">
                <label className="text-gray-400 text-sm block mb-1">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  className="w-full bg-gray-700 text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div className="mb-2">
                <label className="text-gray-400 text-sm block mb-1">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  required
                  minLength={6}
                  className="w-full bg-gray-700 text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              {/* 🆕 2025.12.19: Forgot Password Link (only on login) */}
              {mode === 'login' && (
                <div className="mb-4 text-right">
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="text-blue-400 hover:text-blue-300 text-sm"
                  >
                    Forgot password?
                  </button>
                </div>
              )}
              
              {mode === 'register' && <div className="mb-4"></div>}
              
              <button
                type="submit"
                disabled={loading}
                className={`w-full font-bold py-3 rounded-lg transition ${
                  loading
                    ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-blue-600 to-teal-600 hover:from-blue-500 hover:to-teal-500 text-white'
                }`}
              >
                {loading ? 'Please wait...' : mode === 'login' ? 'Login' : 'Create Account'}
              </button>
            </form>

            {/* Skip for now */}
            <button
              onClick={() => onAuthSuccess(null)}
              className="w-full mt-4 text-gray-500 hover:text-gray-400 text-sm"
            >
              Continue as Guest
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthScreen;
